import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import Modal from '../components/modal/Modal';

import { useBrandContext } from '../context/BrandContext';
import { useSocketContext } from '../websocket/websocketProvider';
import { ReadyState } from 'react-use-websocket';
import { useAuth } from '../context/AuthContext';
import { useToastStore } from '../hooks/useToastStore';
import { useVehicleStore } from '../hooks/useVehicleStore';
import {
  AutoChargeAccept,
  AutoChargeConfirmed,
} from '../features/autoRecharge/autoRecharge';
import api from '../axios/axiosConfig';
import apiAxios from '../axios/axiosConfig';

interface ToastProps {
  isOpen?: boolean;
  title?: string;
  onClose?: () => void;
  allowButtonClosed?: boolean;
  handleEnterButton?: () => void;
  children?: any;
  textEnterButton?: string;
  messageModal?: string;
  isLoadingEnterButton?: boolean;
  closeButtonText?: string;
  styleEnterButton?: React.CSSProperties;
  imageEnterButton?: React.ReactNode;
  header?: boolean;
}

interface ToastContextType {
  showToast: (config?: Partial<ToastProps>) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<ToastProps> = ({ children }) => {
  const socketContext = useSocketContext();
  const brandContext = useBrandContext();
  const authContext = useAuth();
  const [modalConnectionVisible, setModalConnectionVisible] =
    useState<boolean>(false);

  const [toast, setToast] = useState<ToastProps | null>({
    isOpen: false,
  });

  const { setShowConnectingToast } = useToastStore();
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [detectedTag, setDetectedTag] = useState<string>();
  const { selectedVehicle, setSelectedVehicle } = useVehicleStore();
  const selectedVehicleRef = useRef(selectedVehicle);

  const showToast = (config?: Partial<ToastProps>) => {
    setToast((prevState) => ({ ...prevState, ...config, isOpen: true }));
  };

  useEffect(() => {
    //console.log('ToastProvider socketReadyState: ' + socketContext.readyState);
    if (
      (authContext.token && socketContext.readyState !== ReadyState.OPEN) ||
      !socketContext.hasConnection
    ) {
      setModalConnectionVisible(true);
      setShowConnectingToast(true);
    } else {
      setModalConnectionVisible(false);
      setShowConnectingToast(false);
    }
  }, [
    socketContext.readyState,
    socketContext.hasConnection,
    authContext.token,
  ]);

  const getVehicles = async () => {
    try {
      const response = await api.get(`/UserVehicles`);
      if (response.data.length > 1) setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  };

  const handleAutoRecharge = async (uvid: string, detectedTag: string) => {
    await getVehicles();
    setSelectedVehicle(uvid);
    setDetectedTag(detectedTag);
  };

  const saveAutoRecharge = async () => {
    let children: any = null;
    try {
      await apiAxios.post(`/ChargeTags/vin`, {
        detectedTag: detectedTag,
        vehicleId: selectedVehicle,
      });
      children = <AutoChargeConfirmed />;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      children = <div>Ocorreu um erro ao cadastrar o veículo</div>;
    } finally {
      setToast({
        children,
        isOpen: true,
        header: false,
        textEnterButton: 'Entendi',
        styleEnterButton: { backgroundColor: brandContext.brandBgColor },
        handleEnterButton: () => {},
      });
    }
  };

  useEffect(() => {
    selectedVehicleRef.current = selectedVehicle;
  }, [selectedVehicle]);

  useEffect(() => {
    if (toast?.isOpen || vehicles.length == 0) return;
    setToast({
      children: <AutoChargeAccept vehicles={vehicles} />,
      isOpen: true,
      header: false,
      textEnterButton: 'Ativar agora!',
      styleEnterButton: { backgroundColor: brandContext.brandBgColor },
      handleEnterButton: saveAutoRecharge,
      allowButtonClosed: true,
      closeButtonText: 'Não, obrigado.',
    });
  }, [vehicles]);

  useEffect(() => {
    if (toast?.isOpen) return;
    let messages = socketContext.messages?.map((jsonString) =>
      JSON.parse(jsonString)
    );
    messages?.forEach((msg) => {
      if (
        msg?.eventType === 'TagDetectedIntegrationEvent' &&
        msg?.detectedTag != null &&
        msg?.vehicleId != null
      ) {
        handleAutoRecharge(msg?.vehicleId, msg?.detectedTag);
      }
    });
  }, [socketContext.messages]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {modalConnectionVisible ? (
        <Modal
          isOpen={modalConnectionVisible}
          title='Conectando...'
          messageModal=''
          dragHandle={false}
        ></Modal>
      ) : (
        toast && (
          <Modal
            isOpen={toast.isOpen}
            title={toast.title}
            onClose={() => { toast.onClose?.(); setToast(null); }}
            allowButtonClosed={toast.allowButtonClosed}
            handleEnterButton={() => {
              toast.handleEnterButton!();
              setToast(null);
            }}
            textEnterButton={toast.textEnterButton}
            messageModal={toast.messageModal}
            closeButtonText={toast.closeButtonText}
            styleEnterButton={
              toast.styleEnterButton
                ? toast.styleEnterButton
                : { backgroundColor: `${brandContext.brandBgColor}` }
            }
            imageEnterButton={toast.imageEnterButton}
            header={toast.header}
          >
            {toast.children}
          </Modal>
        )
      )}
    </ToastContext.Provider>
  );
};
